import React, { useState, useEffect } from 'react';
import { theme, alpha } from '../../../theme';
import { useReports } from '../../../hooks/venue/useReports';
import { useOwnerComplex } from '../../../hooks/venue/useOwnerComplex';
import {
  BookingType,
  BookingStatus,
  BOOKING_COLORS,
  CashRegisterMovement,
} from '../../../types/venue';
import {
  DollarSign,
  Calendar,
  TrendingUp,
  Download,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const ReportsPage: React.FC = () => {
  const {
    summary,
    movements,
    venues,
    loading,
    error,
    filters,
    fetchCashRegister,
    exportToCsv,
    updateFilters,
    resetFilters,
  } = useReports();

  const { complex, fetchComplex } = useOwnerComplex();

  const [dateFrom, setDateFrom] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');
  const [selectedType, setSelectedType] = useState<BookingType | ''>('');
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchComplex();
  }, [fetchComplex]);

  useEffect(() => {
    fetchCashRegister({
      dateFrom,
      dateTo,
      venueId: selectedVenueId || undefined,
      bookingType: selectedType || undefined,
    });
  }, [dateFrom, dateTo, selectedVenueId, selectedType, fetchCashRegister]);

  const handleExport = async () => {
    setExporting(true);
    await exportToCsv('90d');
    setExporting(false);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchCashRegister({
      dateFrom,
      dateTo,
      venueId: selectedVenueId || undefined,
      bookingType: selectedType || undefined,
    });
  };

  const handleResetFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
    setSelectedVenueId('');
    setSelectedType('');
    setCurrentPage(1);
    resetFilters();
  };

  // Filter and paginate movements
  const filteredMovements = movements;
  const totalPages = Math.ceil(filteredMovements.length / itemsPerPage);
  const paginatedMovements = filteredMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '16px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const headerActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const statsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px',
    marginBottom: '24px',
  };

  const statCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    border: `1px solid ${theme.colors.border}`,
  };

  const statHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  };

  const statIconStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const statLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '4px',
  };

  const statValueStyle: React.CSSProperties = {
    fontSize: '1.8rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const filtersStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
    padding: '20px 24px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '24px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  };

  const filterGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  };

  const filterLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const inputStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeSmall,
    minWidth: '140px',
  };

  const tableContainerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
  };

  const tableHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
  };

  const tableTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: theme.colors.background,
    borderBottom: `2px solid ${theme.colors.border}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: '14px 16px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.text,
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const typeBadgeStyle = (type: BookingType): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: theme.fonts.weightBold,
    backgroundColor: `${BOOKING_COLORS[type]}20`,
    color: BOOKING_COLORS[type],
  });

  const statusBadgeStyle = (status: BookingStatus): React.CSSProperties => {
    const colors: Record<BookingStatus, string> = {
      PENDING: theme.colors.warning,
      CONFIRMED: theme.colors.primary,
      COMPLETED: theme.colors.success,
      CANCELLED: theme.colors.danger,
      NO_SHOW: theme.colors.textSecondary,
    };
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: theme.fonts.weightBold,
      backgroundColor: `${colors[status]}20`,
      color: colors[status],
    };
  };

  const paginationStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const paginationInfoStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const paginationButtonsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const paginationButtonStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: theme.borderRadius.button,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: 'transparent',
    color: theme.colors.text,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: theme.fonts.sizeSmall,
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
    color: theme.colors.danger,
    marginBottom: '24px',
  };

  const emptyStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '48px 24px',
    color: theme.colors.textSecondary,
  };

  const getTypeLabel = (type: BookingType) => {
    switch (type) {
      case 'ONLINE':
        return { label: 'Online', emoji: '🌐' };
      case 'FIXED':
        return { label: 'Fijo', emoji: '🔄' };
      case 'MANUAL':
        return { label: 'Manual', emoji: '✍️' };
    }
  };

  const getStatusLabel = (status: BookingStatus) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'CONFIRMED':
        return 'Confirmada';
      case 'COMPLETED':
        return 'Completada';
      case 'CANCELLED':
        return 'Cancelada';
      case 'NO_SHOW':
        return 'No asistió';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (loading && movements.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Cargando reportes...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleRowStyle}>
          <div>
            <h1 style={titleStyle}>REPORTES Y CAJA</h1>
            <p style={subtitleStyle}>
              {complex?.name || 'Mi Complejo'} - Análisis de ingresos y movimientos
            </p>
          </div>
          <div style={headerActionsStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={() =>
                fetchCashRegister({
                  dateFrom,
                  dateTo,
                  venueId: selectedVenueId || undefined,
                  bookingType: selectedType || undefined,
                })
              }
              disabled={loading}
            >
              <RefreshCw size={18} />
              Actualizar
            </button>
            <button
              style={buttonStyle}
              onClick={handleExport}
              disabled={exporting}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00D96F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.primary;
              }}
            >
              <Download size={18} />
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </button>
          </div>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Stats Cards */}
      <div style={statsGridStyle}>
        <div style={statCardStyle}>
          <div style={statHeaderStyle}>
            <div>
              <div style={statLabelStyle}>Ingresos Totales</div>
              <div style={{ ...statValueStyle, color: theme.colors.success }}>
                {formatCurrency(summary.totalRevenue)}
              </div>
            </div>
            <div style={{ ...statIconStyle, backgroundColor: `${alpha('success', '20')}` }}>
              <DollarSign size={20} color={theme.colors.success} />
            </div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statHeaderStyle}>
            <div>
              <div style={statLabelStyle}>Total Reservas</div>
              <div style={statValueStyle}>{summary.totalBookings}</div>
            </div>
            <div style={{ ...statIconStyle, backgroundColor: `${alpha('primary', '20')}` }}>
              <Calendar size={20} color={theme.colors.primary} />
            </div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statHeaderStyle}>
            <div>
              <div style={statLabelStyle}>Online</div>
              <div style={{ ...statValueStyle, color: BOOKING_COLORS.ONLINE }}>
                {summary.onlineBookings}
              </div>
            </div>
            <div style={{ ...statIconStyle, backgroundColor: `${BOOKING_COLORS.ONLINE}20` }}>
              <span style={{ fontSize: '20px' }}>🌐</span>
            </div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statHeaderStyle}>
            <div>
              <div style={statLabelStyle}>Turnos Fijos</div>
              <div style={{ ...statValueStyle, color: BOOKING_COLORS.FIXED }}>
                {summary.fixedBookings}
              </div>
            </div>
            <div style={{ ...statIconStyle, backgroundColor: `${BOOKING_COLORS.FIXED}20` }}>
              <span style={{ fontSize: '20px' }}>🔄</span>
            </div>
          </div>
        </div>

        <div style={statCardStyle}>
          <div style={statHeaderStyle}>
            <div>
              <div style={statLabelStyle}>Manuales</div>
              <div style={{ ...statValueStyle, color: BOOKING_COLORS.MANUAL }}>
                {summary.manualBookings}
              </div>
            </div>
            <div style={{ ...statIconStyle, backgroundColor: `${BOOKING_COLORS.MANUAL}20` }}>
              <span style={{ fontSize: '20px' }}>✍️</span>
            </div>
          </div>
        </div>

        {summary.occupancyRate !== undefined && (
          <div style={statCardStyle}>
            <div style={statHeaderStyle}>
              <div>
                <div style={statLabelStyle}>Ocupación</div>
                <div style={statValueStyle}>{summary.occupancyRate}%</div>
              </div>
              <div style={{ ...statIconStyle, backgroundColor: `${alpha('warning', '20')}` }}>
                <TrendingUp size={20} color={theme.colors.warning} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={filtersStyle}>
        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Desde</label>
          <input
            type="date"
            style={{ ...inputStyle, colorScheme: 'dark' }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Hasta</label>
          <input
            type="date"
            style={{ ...inputStyle, colorScheme: 'dark' }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        {venues.length > 1 && (
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Cancha</label>
            <select
              style={inputStyle}
              value={selectedVenueId}
              onChange={(e) => setSelectedVenueId(e.target.value)}
            >
              <option value="">Todas</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Tipo</label>
          <select
            style={inputStyle}
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value as BookingType | '')}
          >
            <option value="">Todos</option>
            <option value="ONLINE">Online</option>
            <option value="FIXED">Turno Fijo</option>
            <option value="MANUAL">Manual</option>
          </select>
        </div>
        <button
          style={{
            ...secondaryButtonStyle,
            padding: '10px 16px',
            fontSize: theme.fonts.sizeSmall,
          }}
          onClick={handleApplyFilters}
        >
          <Filter size={16} />
          Aplicar
        </button>
        <button
          style={{
            ...secondaryButtonStyle,
            padding: '10px 16px',
            fontSize: theme.fonts.sizeSmall,
            backgroundColor: 'transparent',
          }}
          onClick={handleResetFilters}
        >
          Limpiar
        </button>
      </div>

      {/* Cash Register Table */}
      <div style={tableContainerStyle}>
        <div style={tableHeaderStyle}>
          <h2 style={tableTitleStyle}>Movimientos de Caja</h2>
          <span style={{ fontSize: theme.fonts.sizeSmall, color: theme.colors.textSecondary }}>
            {filteredMovements.length} registros
          </span>
        </div>

        {filteredMovements.length === 0 ? (
          <div style={emptyStyle}>No hay movimientos en el período seleccionado</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Fecha</th>
                    <th style={thStyle}>Hora</th>
                    <th style={thStyle}>Cancha</th>
                    <th style={thStyle}>Cliente</th>
                    <th style={thStyle}>Tipo</th>
                    <th style={thStyle}>Estado</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedMovements.map((movement) => {
                    const typeInfo = getTypeLabel(movement.bookingType);
                    return (
                      <tr key={movement.id}>
                        <td style={tdStyle}>{movement.date}</td>
                        <td style={tdStyle}>{movement.time}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: theme.fonts.weightBold }}>
                            {movement.venue}
                          </div>
                          <div
                            style={{ fontSize: '11px', color: theme.colors.textSecondary }}
                          >
                            {movement.venueType.replace('FUTBOL_', 'F')}
                          </div>
                        </td>
                        <td style={tdStyle}>{movement.customerName || '-'}</td>
                        <td style={tdStyle}>
                          <span style={typeBadgeStyle(movement.bookingType)}>
                            {typeInfo.emoji} {typeInfo.label}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={statusBadgeStyle(movement.status)}>
                            {getStatusLabel(movement.status)}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <span
                            style={{
                              fontWeight: theme.fonts.weightBold,
                              color:
                                movement.status === 'CANCELLED'
                                  ? theme.colors.danger
                                  : theme.colors.success,
                            }}
                          >
                            {movement.status === 'CANCELLED'
                              ? '-'
                              : formatCurrency(movement.amount)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={paginationStyle}>
                <div style={paginationInfoStyle}>
                  Mostrando {(currentPage - 1) * itemsPerPage + 1} -{' '}
                  {Math.min(currentPage * itemsPerPage, filteredMovements.length)} de{' '}
                  {filteredMovements.length}
                </div>
                <div style={paginationButtonsStyle}>
                  <button
                    style={paginationButtonStyle}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </button>
                  <span
                    style={{
                      padding: '8px 16px',
                      fontSize: theme.fonts.sizeSmall,
                      color: theme.colors.text,
                    }}
                  >
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    style={paginationButtonStyle}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Siguiente
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
